import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link2, Copy, ExternalLink, Download, Image as ImageIcon } from 'lucide-react';
import OsScroll from './ui/OsScroll';
import { useChatStore } from '../store/chatStore';
import { useAuthStore } from '../store/authStore';
import { deliverChatMessage } from '../services/chatSend';
import { channelDisplay, dmOther, dmSubjectId } from './chatHelpers';
import ContextMenu from './ui/ContextMenu';
import { copyTextToClipboard, copyImageToClipboard, downloadImage } from '../utils/clipboard';
import UserAvatar from './ui/UserAvatar';
import AvatarStatusBadge from './ui/AvatarStatusBadge';
import ChannelAvatar from './ui/ChannelAvatar';
import { loadOlderPage, OLDER_PAGE_LIMIT } from '../utils/chatOlderPage';
import ChatHeaderActions from './chat/ChatHeaderActions';
import ChatSearchResults from './chat/ChatSearchResults';
import ChatJumpToPresent from './chat/ChatJumpToPresent';
import useJumpToMessage from '../hooks/useJumpToMessage';
import { useChatSearchStore } from '../store/chatSearchStore';
import { usePreferencesStore } from '../store/preferencesStore';
import ChannelMembersPopover from './ChannelMembersPopover';
import useDelayedVisible from '../hooks/useDelayedVisible';
import './Chat.css';
import { EMPTY_ARRAY, GROUP_WINDOW_MS, dayKey, dayLabel, newUid } from './chat/chatMessageUtils';
import { MessagesSkeleton, TypingStrip } from './chat/ChatViewPlaceholders';
import { MessageRow } from './chat/MessageRow';
import { openExternalUrl } from './chat/chatLinks';
import { MessageContent } from './chat/MessageContent';
import { Composer } from './chat/ChatComposer';

/**
 * The message list scrolls vertically only. Rows are built to fit the list
 * (Chat.css: `overflow-wrap: anywhere`, capped media, an ellipsised author);
 * this is the backstop, so content that still slips through is clipped instead
 * of growing a horizontal scrollbar under the list (owner, 1.1.2). Popovers —
 * emoji picker, forward picker, profile card, tooltips — are portalled to
 * <body>, so nothing they show is clipped. Module constant: OsScroll memoises
 * on the options object.
 */
const MESSAGE_LIST_SCROLL = { overflow: { x: 'hidden', y: 'scroll' } };

/**
 * The active conversation: header (name + who's online), a scrolling message
 * list (older messages page in on scroll-to-top; the in-memory buffer is capped
 * in chatStore), a typing indicator, and a composer supporting text, code
 * blocks, image/file attachments, and Board/Collection/Document references.
 */
export default function ChatMessageView() {
  // Selectors are deliberately narrowed to the ACTIVE channel's slices —
  // subscribing to the whole messagesByChannel/typingByChannel maps made every
  // background-channel event and typing ping re-render (and re-reconcile) the
  // entire list. Slices keep identity unless this channel actually changed.
  const activeChannelId = useChatStore((s) => s.activeChannelId);
  const channel = useChatStore((s) => s.channels.find((c) => c.id === s.activeChannelId) || null);
  const messages = useChatStore((s) => s.messagesByChannel[s.activeChannelId]) || EMPTY_ARRAY;
  const online = useChatStore((s) => s.presenceByChannel[s.activeChannelId]) || EMPTY_ARRAY;
  const loadingHistory = useChatStore((s) => Boolean(s.historyLoading[s.activeChannelId]));
  const currentUserId = useChatStore((s) => s.currentUserId);

  // Skeletons are for loads that are actually SLOW. A fast first page (and
  // every empty/new conversation, which resolves near-instantly) renders
  // nothing during the grace window and then goes straight to its real
  // state — no shimmer flash. Once shown it holds briefly so it can't
  // flicker out a frame later.
  const showHistorySkeleton = useDelayedVisible(loadingHistory && messages.length === 0, 400, 500);

  // Fade the history in when a cold load lands (0 -> N while the fetch was
  // running) — content softly appearing beats an abrupt pop, whether it
  // replaced a skeleton or arrived within the instaload window. Deliberately
  // NOT triggered by cached-channel switches (they must feel instant) or by
  // sending the first message into an empty conversation (loading was false).
  // Render-time ref bookkeeping: purely derived from the previous render.
  const listAnimRef = useRef({ id: null, count: 0, loading: false, enter: false });
  {
    const a = listAnimRef.current;
    if (a.id !== activeChannelId) {
      listAnimRef.current = {
        id: activeChannelId,
        count: messages.length,
        loading: loadingHistory,
        enter: false,
      };
    } else {
      if (a.count === 0 && messages.length > 0 && a.loading) a.enter = true;
      a.count = messages.length;
      a.loading = loadingHistory;
    }
  }
  const listEnter = listAnimRef.current.enter;
  const addMessage = useChatStore((s) => s.addMessage);
  const updateMessage = useChatStore((s) => s.updateMessage);
  const user = useAuthStore((s) => s.user);

  // Reciprocity: hiding your own read receipts also hides everyone else's
  // (matching WhatsApp/Telegram) — the sender side of that deal is enforced
  // on the server, this is the viewer side.
  const receiptsOn = usePreferencesStore((s) => s.readReceipts !== false);

  // A message of mine counts as read once every member who SHARES receipts
  // (last_read_seq present — the server nulls it for members who hide theirs)
  // has advanced past it, i.e. seq <= min(visible cursors). DMs reduce to
  // "the other person read it"; a group where everyone hides receipts never
  // shows the double check, which is the only honest rendering.
  const readCutoff = useMemo(() => {
    if (!channel || !receiptsOn) return null;
    const others = (channel.members || []).filter(
      (mm) => mm.user_id !== currentUserId && mm.last_read_seq != null
    );
    if (!others.length) return null;
    return others.reduce((min, mm) => Math.min(min, mm.last_read_seq || 0), Infinity);
  }, [channel, currentUserId, receiptsOn]);

  // Right-click menu over the message list: { x, y, items } or null.
  const [ctxMenu, setCtxMenu] = useState(null);
  // Members roster/invite popover, anchored to the header's member count.
  const [membersOpen, setMembersOpen] = useState(false);
  const membersBtnRef = useRef(null);

  const osRef = useRef(null);
  const atBottomRef = useRef(true);
  const msgListRef = useRef(null);
  // channelId -> distance-from-bottom (scrollHeight - scrollTop) captured on
  // every scroll, so returning to a conversation restores the exact reading
  // position instead of always snapping to the bottom.
  const scrollMemRef = useRef(new Map());
  // When a page of older messages is prepended, we restore scroll position by
  // pinning distance-from-bottom (which is invariant to anything added above).
  // The value is captured before the fetch and applied synchronously in a
  // layout effect after the prepend, so no intermediate frame is painted.
  const restoreFromBottomRef = useRef(null);
  // Synchronous in-flight guard. State updates are async, so a burst of scroll
  // events in the same tick would all read a stale `loadingOlder === false` and
  // each fire a fetch; a ref flips immediately and closes that window.
  const loadingOlderRef = useRef(false);
  // Channels whose full history has been loaded — once exhausted, scrolling to
  // the top must stop re-requesting empty pages.
  const exhaustedRef = useRef(new Set());
  const { t } = useTranslation();
  const [loadingOlder, setLoadingOlder] = useState(false);
  const historyWindowDays = useChatStore((s) =>
    activeChannelId ? s.historyWindowByChannel[activeChannelId] ?? null : null
  );
  // The window is a fact about the CHANNEL (its oldest message is older than
  // the cutoff), so the very first page already reports it. Saying "your plan
  // shows the last 90 days" above the newest fifty messages, while ninety days
  // of scrollback is still there to fetch, is simply wrong — wait until there
  // is nothing left to fetch.
  const historyEnded = useChatStore((s) =>
    activeChannelId ? Boolean(s.historyEndByChannel[activeChannelId]) : false
  );
  const showHistoryWindow = historyWindowDays != null && historyEnded && !loadingOlder;
  // Paging skeleton only for slow older-page fetches (fast ones just anchor
  // the prepended page in with no indicator, which reads as instant). The
  // composite gate dies the moment the fetch settles or the channel switches,
  // so a delayed/held skeleton can never paint stale.
  const showOlderSkeletonRaw = useDelayedVisible(loadingOlder, 300, 0);
  const olderSkelShown = showOlderSkeletonRaw && loadingOlder;

  const getViewport = useCallback(
    () => osRef.current?.osInstance()?.elements()?.viewport || null,
    []
  );
  // Search results panel + jump-to-message (components/chat, hooks/useJumpToMessage).
  const searchOpen = useChatSearchStore((s) => s.open && s.channelId === activeChannelId);
  const jump = useJumpToMessage({ channelId: activeChannelId, getViewport, loadingOlderRef, exhaustedRef, atBottomRef, restoreFromBottomRef });

  // Conversation switch: drop the previous channel's in-flight scroll intent,
  // then position the viewport SYNCHRONOUSLY (before paint) — either back to
  // where the user last was in this conversation, or at the bottom. This used
  // to run post-paint, so the first frame of channel B painted at channel A's
  // stale scrollTop and visibly jumped — the main "switch flash".
  useLayoutEffect(() => {
    restoreFromBottomRef.current = null;
    loadingOlderRef.current = false;
    // The STATE flag must reset too — a fetch abandoned by switching away
    // resolves against the old channel and deliberately skips this reset, so
    // without it the paging skeleton would stick to the next conversation.
    setLoadingOlder(false);
    // The buffer may have been trimmed/replaced since this channel was last
    // open, so a previous "full history loaded" verdict no longer applies —
    // give scroll-up paging a fresh chance (a truly exhausted channel just
    // re-learns it from one short page).
    exhaustedRef.current.delete(activeChannelId);
    useChatStore.getState().clearReplyTarget();

    let raf = 0;
    let tries = 0;
    const position = () => {
      const vp = getViewport();
      if (vp && vp.scrollHeight > 0) {
        const remembered = scrollMemRef.current.get(activeChannelId);
        // A remembered spot deeper than the (possibly trim-on-leave-shrunk)
        // buffer can't be restored — drop it and land at the bottom instead
        // of dumping the user at the top of a shorter list.
        if (remembered != null && remembered > vp.scrollHeight) {
          scrollMemRef.current.delete(activeChannelId);
        }
        if (
          remembered != null &&
          remembered <= vp.scrollHeight &&
          remembered - vp.clientHeight >= 60
        ) {
          vp.scrollTop = vp.scrollHeight - remembered;
          atBottomRef.current = false;
        } else {
          vp.scrollTop = vp.scrollHeight;
          atBottomRef.current = true;
        }
        return;
      }
      // OverlayScrollbars initialises deferred, so the viewport may not exist
      // on the first render — retry across a few frames until it does, then
      // give up (bounded so this can never spin).
      if (tries++ < 20) raf = requestAnimationFrame(position);
    };
    position();
    return () => cancelAnimationFrame(raf);
  }, [activeChannelId, getViewport]);

  // Restore scroll position immediately after older messages are prepended,
  // before the browser paints — prevents the visible jump/glitch.
  useLayoutEffect(() => {
    const vp = getViewport();
    if (vp && restoreFromBottomRef.current != null) {
      vp.scrollTop = vp.scrollHeight - restoreFromBottomRef.current;
      restoreFromBottomRef.current = null;
    }
  }, [messages, getViewport]);

  // The older-page skeleton sits IN-FLOW above the messages (unlike the old
  // overlay spinner), so its mount/unmount changes scrollHeight. WKWebView has
  // no native scroll anchoring — offset scrollTop by the skeleton's height in
  // the same pre-paint pass to keep the reading position pixel-stable both
  // ways. Channel-tagged so a switch mid-fetch can't apply a stale offset to
  // the next conversation's freshly positioned viewport.
  const olderSkelRef = useRef(null);
  const olderSkelStateRef = useRef({ h: 0, channelId: null });
  const prevOlderShownRef = useRef(false);
  useLayoutEffect(() => {
    const was = prevOlderShownRef.current;
    prevOlderShownRef.current = olderSkelShown;
    const vp = getViewport();
    if (olderSkelShown && !was) {
      const h = olderSkelRef.current?.offsetHeight || 0;
      olderSkelStateRef.current = { h, channelId: activeChannelId };
      if (vp && h) vp.scrollTop += h;
    } else if (!olderSkelShown && was) {
      const { h, channelId } = olderSkelStateRef.current;
      if (vp && h && channelId === activeChannelId) vp.scrollTop -= h;
      olderSkelStateRef.current = { h: 0, channelId: null };
    }
  }, [olderSkelShown, activeChannelId, getViewport]);

  // Autoscroll to bottom on new messages when the user is already near bottom
  // — synchronously, so the appended message never paints at the old offset.
  // (During an older-page prepend the user is at the top, so atBottomRef is
  // false and this correctly no-ops — the layout effect above owns that case.)
  useLayoutEffect(() => {
    if (!atBottomRef.current) return;
    const vp = getViewport();
    if (vp) vp.scrollTop = vp.scrollHeight;
  }, [messages.length, getViewport]);

  // Keep the bottom pinned while content GROWS after render — link-preview
  // cards resolving, images decoding, video embeds mounting. Those all land
  // after the scroll-to-bottom and used to push the newest message out of
  // frame. Only acts while the user is actually at the bottom.
  // Re-armed per channel: the OsScroll tree doesn't exist while no channel is
  // selected (the empty state renders instead), so a mount-once observer
  // would never attach on the open-chat-then-pick-a-conversation flow.
  // Observes the message LIST, not OverlayScrollbars' `elements().content`:
  // without `elements.content` configured that is the viewport itself, whose
  // box never changes when a message grows — so a reaction added to the last
  // message stayed cut off below the fold (owner, 24.09.2026).
  useEffect(() => {
    if (!activeChannelId) return undefined;
    let ro = null;
    let raf = 0;
    let tries = 0;
    const attach = () => {
      const content = msgListRef.current;
      if (content && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => {
          if (!atBottomRef.current) return;
          const vp = getViewport();
          if (vp) vp.scrollTop = vp.scrollHeight;
        });
        ro.observe(content);
        return;
      }
      if (tries++ < 40) raf = requestAnimationFrame(attach);
    };
    attach();
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [activeChannelId, getViewport]);

  const onScroll = useCallback(
    async (instance) => {
      const vp = instance?.elements()?.viewport || getViewport();
      if (!vp) return;
      atBottomRef.current = vp.scrollHeight - vp.scrollTop - vp.clientHeight < 60;
      jump.onScrolled(atBottomRef.current);
      // Reading position for restore-on-return, as distance-from-bottom — the
      // one measure that survives the buffer being top-trimmed while away.
      scrollMemRef.current.set(activeChannelId, vp.scrollHeight - vp.scrollTop);

      const buf = useChatStore.getState().messagesByChannel[activeChannelId] || [];
      if (
        vp.scrollTop < 40 &&
        !loadingOlderRef.current &&
        !exhaustedRef.current.has(activeChannelId) &&
        buf.length > 0
      ) {
        const oldestSeq = buf[0]?.server_seq;
        if (!oldestSeq) return;
        loadingOlderRef.current = true;
        setLoadingOlder(true);
        const fromBottom = vp.scrollHeight - vp.scrollTop;
        try {
          const { exhausted } = await loadOlderPage(activeChannelId, oldestSeq, OLDER_PAGE_LIMIT, {
            // Only anchor the visible viewport if we're still on the channel we
            // fetched for — otherwise just fold the page into its (off-screen)
            // buffer without touching the now-current channel's scroll.
            beforePrepend: () => {
              if (useChatStore.getState().activeChannelId === activeChannelId) {
                restoreFromBottomRef.current = fromBottom;
              }
            },
          });
          if (exhausted) exhaustedRef.current.add(activeChannelId);
        } catch {
          /* ignore */
        } finally {
          // Only release the guard if we're still on the channel we fetched for;
          // otherwise the channel-change effect already owns the reset and a late
          // resolve here must not clear the new channel's in-flight fetch.
          if (useChatStore.getState().activeChannelId === activeChannelId) {
            loadingOlderRef.current = false;
            setLoadingOlder(false);
          }
        }
      }
    },
    [activeChannelId, getViewport, jump]
  );

  const send = useCallback(
    (body, kind, metadata) => {
      if (!activeChannelId) return;
      const uid = newUid();
      // A pending reply rides along as metadata.reply_to on whatever's being
      // sent (text, code, an attachment caption, ...) — consumed once, then
      // cleared so it doesn't stick to the next message.
      const replyTo = useChatStore.getState().replyTarget;
      const finalMetadata = replyTo ? { ...(metadata || {}), reply_to: replyTo } : metadata || {};
      const optimistic = {
        uid,
        channel_id: activeChannelId,
        sender_id: currentUserId,
        sender: { id: currentUserId, name: user?.name },
        body: body || '',
        kind: kind || 'text',
        metadata: finalMetadata,
        inserted_at: new Date().toISOString(),
      };
      atBottomRef.current = true;
      addMessage(activeChannelId, optimistic);
      if (replyTo) useChatStore.getState().clearReplyTarget();
      // WS first, REST when the socket cannot carry it (services/chatSend.js).
      deliverChatMessage(activeChannelId, {
        uid,
        body: optimistic.body,
        kind: optimistic.kind,
        metadata: optimistic.metadata,
      })
        .then((ack) => {
          // The broadcast echo usually reconciles first; merging the ack too
          // covers the echo racing a channel switch or a dropped frame.
          updateMessage(activeChannelId, { uid, id: ack.id, server_seq: ack.server_seq, failed: false });
        })
        .catch(() => {
          // Surface the failure on the message (checkmark area turns into a
          // retry affordance) instead of silently pretending it sent. Retry
          // is idempotent on uid, so flagging is always safe.
          updateMessage(activeChannelId, { uid, failed: true });
        });
    },
    [activeChannelId, currentUserId, user?.name, addMessage, updateMessage]
  );

  if (!channel) {
    return <div className="chat-view"><div className="chat-empty">Select a conversation to start chatting.</div></div>;
  }

  const { name, seed } = channelDisplay(channel, currentUserId);

  // Context menu over messages: entries depend on what was right-clicked —
  // selected text (Copy), a link (Open/Copy address), an attachment image
  // (Copy/Save image). Anything else keeps the default (no) menu.
  const onMessagesContextMenu = (e) => {
    const link = e.target.closest?.('a[href]');
    const img = e.target.closest?.('img.chat-attach-img');
    const selection = String(window.getSelection() || '');
    const items = [];
    if (selection.trim()) {
      items.push({
        icon: <Copy size={14} />,
        label: 'Copy',
        onClick: () => copyTextToClipboard(selection),
      });
    }
    if (link?.href) {
      items.push({
        icon: <ExternalLink size={14} />,
        label: 'Open link',
        onClick: () => openExternalUrl(link.href),
      });
      items.push({
        icon: <Link2 size={14} />,
        label: 'Copy link address',
        onClick: () => copyTextToClipboard(link.href),
      });
    }
    if (img?.src) {
      items.push({
        icon: <ImageIcon size={14} />,
        label: 'Copy image',
        onClick: () => copyImageToClipboard(img.src),
      });
      items.push({
        icon: <Download size={14} />,
        label: 'Save image',
        onClick: () => downloadImage(img.src, img.alt || 'image'),
      });
    }
    if (!items.length) return;
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, items });
  };

  return (
    <div className="chat-view">
      <div className="chat-view-header">
        {channel.kind === 'dm' ? (
          <UserAvatar
            className="chat-avatar chat-avatar--sm"
            userId={dmOther(channel, currentUserId)?.user_id}
            name={name}
          >
            {/* `.chat-avatar--sm` is 28px in Chat.css. */}
            <AvatarStatusBadge userId={dmSubjectId(channel, currentUserId)} avatarSize={28} />
          </UserAvatar>
        ) : (
          <ChannelAvatar className="chat-avatar chat-avatar--sm" channel={channel} name={name} seed={seed} />
        )}
        <div style={{ minWidth: 0 }}>
          <div className="chat-view-title">{name}</div>
          {/* The whole subtitle is a group feature. In a 1:1 "2 members" and
              "N online" both state the obvious — it is you and the person named
              directly above, whose status dot is on the avatar beside it — and
              the members popover this button opens would list exactly those two
              people. So the line is not rendered at all for a dm. */}
          {channel.kind !== 'dm' && (
            <button
              ref={membersBtnRef}
              type="button"
              className="chat-view-sub chat-view-sub--btn"
              title="View members"
              aria-haspopup="dialog"
              aria-expanded={membersOpen}
              onClick={() => setMembersOpen((o) => !o)}
            >
              {(channel.members || []).length} member{(channel.members || []).length === 1 ? '' : 's'}
              {online.length > 0 && ` · ${online.length} online`}
            </button>
          )}
        </div>
        <ChatHeaderActions channel={channel} channelName={name} currentUserId={currentUserId} />
      </div>

      <ChannelMembersPopover
        open={membersOpen}
        anchorRef={membersBtnRef}
        channel={channel}
        currentUserId={currentUserId}
        online={online}
        onClose={() => setMembersOpen(false)}
      />

      <div className="chat-view-split">
        <div className="chat-view-main">
          <div className="chat-msg-scroll-wrap" onContextMenu={onMessagesContextMenu}>
            <OsScroll className="chat-msg-scroll" ref={osRef} options={MESSAGE_LIST_SCROLL} events={{ scroll: onScroll }}>
              <div ref={msgListRef} className={`chat-msg-list${listEnter ? ' chat-msg-list--enter' : ''}`}>
                {olderSkelShown && (
                  <div ref={olderSkelRef} className="chat-skel-paging">
                    <MessagesSkeleton rows={3} />
                  </div>
                )}
                {/* The plan serves only the last N days of this conversation.
                    Shown at the top of what was loaded, so history stopping reads
                    as a plan limit rather than as messages that went missing —
                    they are all still on the server. */}
                {showHistoryWindow && (
                  <div className="chat-history-window" role="status">
                    {t('chat.historyWindow', {
                      days: historyWindowDays,
                      defaultValue: 'Your plan shows the last {{days}} days of this conversation. Older messages are kept — upgrade to read them.',
                    })}
                  </div>
                )}
                {messages.length === 0 && showHistorySkeleton && <MessagesSkeleton />}
                {messages.length === 0 && !loadingHistory && !showHistorySkeleton && (
                  <div className="chat-msg-list-empty">No messages yet — say hi.</div>
                )}
                {messages.map((m, i) => {
                  const prev = messages[i - 1];
                  const showDivider =
                    m.inserted_at &&
                    (!prev || dayKey(new Date(prev.inserted_at)) !== dayKey(new Date(m.inserted_at)));
                  // Discord-style grouping: same author within the window, no day
                  // divider in between, and not a reply (replies re-show the
                  // header so the quote has an author to hang off).
                  const grouped = Boolean(
                    !showDivider &&
                      prev &&
                      prev.sender_id != null &&
                      prev.sender_id === m.sender_id &&
                      !m.metadata?.reply_to &&
                      new Date(m.inserted_at) - new Date(prev.inserted_at) < GROUP_WINDOW_MS
                  );
                  return (
                    <React.Fragment key={m.uid || m.id || m.server_seq}>
                      {showDivider && (
                        <div className="chat-day-divider">
                          <span className="chat-day-divider-label">{dayLabel(m.inserted_at)}</span>
                        </div>
                      )}
                      <MessageRow message={m} grouped={grouped} readCutoff={readCutoff} />
                    </React.Fragment>
                  );
                })}
              </div>
            </OsScroll>
          </div>

          <TypingStrip channelId={activeChannelId} />

          <Composer channelId={activeChannelId} onSend={send} />
          <ChatJumpToPresent onJump={jump.jumpToPresent} />
        </div>
        {searchOpen && <ChatSearchResults channel={channel} />}
      </div>

      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={ctxMenu.items}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

// Named export for wiring tests (appointmentShareCard.test.js drives the real
// kind/share_type dispatch through this, per the aiRichResultWiring rule).
export { MessageContent };
